/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';
import Media from 'react-media';

import { Typography, Typography2, breakpoints, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import Carousel from 'bundles/design-system/components/Carousel';
import type { CarouselSettings } from 'bundles/design-system/components/types';
import type {
  EntWebsiteAcademyModuleFragment,
  EntWebsiteBlockAcademyQuery,
  ImageFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout, { noImage } from 'bundles/ent-website/contentful/core/BlockLayout';
import AcademyCard from 'bundles/ent-website/contentful/modules/AcademyModule/AcademyCard';
import Arrow from 'bundles/ent-website/contentful/modules/AcademyModule/Arrow';
import { entWebsiteBlockAcademyQuery } from 'bundles/ent-website/queries/EntWebsiteAcademy';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';

import 'css!./__styles__/AcademyModule';

export const MOBILE_SINGLE = 520;
export const MOBILE_DOUBLE = 800;
export const TABLET_TRIPLE = 1300;

type Props = BlockModuleInnerProps<EntWebsiteAcademyModuleFragment>;

type FilteredAcademy = {
  image: ImageFragment;
  academyTitle: string;
  description: string;
  starRating: number;
  reviewCount: string;
  academyUrl: string;
};

const styles: Record<string, Interpolation<Theme>> = {
  eyebrow: () => css`
    font-size: 20px !important;
    line-height: 150%;
    letter-spacing: -0.03em;
    font-weight: 600;
    text-transform: uppercase;
    margin-bottom: var(--cds-spacing-300);

    ${breakpoints.down('xs')} {
      font-size: 14px !important;
    }
  `,

  headline: () => css`
    margin-bottom: var(--cds-spacing-300);
    font-weight: 600;
  `,

  container: css`
    display: flex;
    flex-direction: column;
    width: 100%;

    .slick-list {
      overflow: hidden;
    }

    .slick-arrow:not(.slick-disabled) {
      cursor: pointer;
    }
  `,
  flexContainer: css`
    width: 100%;
    position: relative;
  `,
  cardContainer: css`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
  `,

  descriptionText: css`
    max-width: 860px;
  `,

  browseTitle: () => css`
    margin-top: var(--cds-spacing-600);
    margin-bottom: var(--cds-spacing-300);

    ${breakpoints.up('md')} {
      margin-top: var(--cds-spacing-1000);
    }
  `,

  mobileCardsContainer: css`
    display: flex;
    flex-direction: column;
  `,
};

export function AcademyModule({ content, assembly }: Props) {
  const { visibility, eyebrow } = assembly;
  const { title, descriptionText, browseTitle, academiesCollection } = content;

  const cdsTheme = useTheme();

  const mobileCarouselSettings: CarouselSettings = {
    dots: true,
    arrows: false,
  };

  const EnterpriseCarouselSettings: CarouselSettings = {
    dots: true,
    arrows: true,
    infinite: false,
    prevArrow: <Arrow behavior="previous" />,
    nextArrow: <Arrow behavior="next" />,
    className: 'rc-CollectionItemWithCarousel-AcademyCarousel',
    dotsClass: 'carousel-dots',
    slidesToShow: 4,
    slidesToScroll: 4,
    isFluid: true,
    // react-slick is super broken in RTL, so we have to use tricks to make it work. This means disabling `lazyLoad`,
    // since it assumes LTR and doesn't even render the things in the viewport, and doing tricks to fixup the position
    // when it loads (see tricks in static/bundles/design-system/components/Carousel.tsx).
    lazyLoad: undefined,
    responsive: [
      {
        breakpoint: cdsTheme.breakpoints.values.xl,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 4,
        },
      },
      {
        breakpoint: TABLET_TRIPLE,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
        },
      },
      {
        breakpoint: MOBILE_DOUBLE,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        },
      },
      {
        breakpoint: MOBILE_SINGLE,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  const contentPanel = () => (
    <div css={styles.container}>
      {eyebrow && (
        <Typography2 component="p" variant="bodyPrimary" css={styles.eyebrow} color="inherit">
          {eyebrow}
        </Typography2>
      )}
      {visibility?.title && title && (
        <Typography variant="d2" css={styles.headline} color="inherit">
          {title}
        </Typography>
      )}
      {visibility?.descriptionText && descriptionText && (
        <Typography variant="h2" component="p" color="inherit" css={styles.descriptionText}>
          {descriptionText}
        </Typography>
      )}
      {visibility?.browseTitle && browseTitle && (
        <Typography css={styles.browseTitle} variant="h1" component="h2" color="inherit">
          {browseTitle}
        </Typography>
      )}

      {academiesCollection?.items && (
        <Media query={{ maxWidth: cdsTheme.breakpoints.values.md }} defaultMatches={false}>
          {(matches) => {
            // Messy way to group by pairs based on index.
            const pairedAcademiesCollection: Array<Array<FilteredAcademy | undefined>> = [];
            const filteredAcademiesCollection = academiesCollection?.items?.filter((academy) => {
              return (
                academy?.image &&
                academy?.academyTitle &&
                academy?.description &&
                academy?.starRating &&
                academy?.reviewCount &&
                academy?.academyUrl
              );
            }) as Array<FilteredAcademy>;

            for (let i = 0; i < filteredAcademiesCollection.length / 2; i += 1) {
              pairedAcademiesCollection.push([
                filteredAcademiesCollection?.[i * 2] ?? undefined,
                filteredAcademiesCollection?.[i * 2 + 1] ?? undefined,
              ]);
            }

            if (matches) {
              return (
                <Carousel settings={{ ...EnterpriseCarouselSettings, ...mobileCarouselSettings }} enableRtl>
                  {pairedAcademiesCollection?.map((academyPair, idx) => {
                    return (
                      <div css={styles.mobileCardsContainer}>
                        {academyPair[0] && (
                          <AcademyCard
                            key={academyPair[0].academyTitle}
                            module={content}
                            index={idx * 2}
                            image={academyPair[0].image}
                            academyTitle={academyPair[0].academyTitle}
                            description={academyPair[0].description}
                            starRating={academyPair[0].starRating}
                            reviewCount={academyPair[0].reviewCount}
                            academyUrl={academyPair[0].academyUrl}
                          />
                        )}

                        {academyPair[1] && (
                          <AcademyCard
                            key={academyPair[1].academyTitle}
                            module={content}
                            index={idx * 2 + 1}
                            image={academyPair[1].image}
                            academyTitle={academyPair[1].academyTitle}
                            description={academyPair[1].description}
                            starRating={academyPair[1].starRating}
                            reviewCount={academyPair[1].reviewCount}
                            academyUrl={academyPair[1].academyUrl}
                          />
                        )}
                      </div>
                    );
                  })}
                </Carousel>
              );
            } else {
              return (
                <Carousel settings={EnterpriseCarouselSettings} enableRtl>
                  {filteredAcademiesCollection?.map((academy, idx) => {
                    return (
                      <AcademyCard
                        index={idx}
                        module={content}
                        key={academy.academyTitle}
                        image={academy.image}
                        academyTitle={academy.academyTitle}
                        description={academy.description}
                        starRating={academy.starRating}
                        reviewCount={academy.reviewCount}
                        academyUrl={academy.academyUrl}
                      />
                    );
                  })}
                </Carousel>
              );
            }
          }}
        </Media>
      )}
    </div>
  );

  return <BlockLayout assembly={noImage(assembly)} renderContent={contentPanel} />;
}

// @ts-expect-error TSMIGRATION-5.0 TS2344
export const BlockAcademyModule = withBlockQuery<EntWebsiteAcademyModuleFragment, EntWebsiteBlockAcademyQuery>(
  entWebsiteBlockAcademyQuery
)(AcademyModule);
